import {starsConfig} from './MainPageAnimation.stars-config';
import {DI_TOKENS} from '@/di/di.tokens';
import {
  AnimatedSprite,
  Application,
  Assets,
  Container,
  ContainerChild,
  Graphics,
  Point,
  Sprite,
  Spritesheet,
  Texture,
} from 'pixi.js';

import anime from 'animejs';
import {injected} from 'brandi';

import {ParticleEmitter} from '@specials/particle-emitter';

import {Config, GlobalWindow} from '@/types';
import {GameObjectUtils} from '@/utils/GameObjectUtils';

const RAYS_DELAY_MIN = 1200;
const RAYS_DELAY_MAX = 2500;
const RAYS_ANIMATION_DURATION = 3000;
const RAYS_SHIFT = 15;
const RAYS_EASING = 'easeInSine';

const SPACESHIP_ANIMATION_DURATION = 2000;
const SPACESHIP_ANIMATION_EASING = 'easeInQuad';
const SPACESHIP_ANIMATION_Y_SHIFT = 2;

interface AnimationAssets {
  particle: Texture;
  fire: Spritesheet;
  kotletka: Spritesheet;
  spaceship: Texture;
  portal: Texture;
}

export class MainPageAnimation {
  private app: Application | null;
  private rootContainer: Container;
  private containerNode: HTMLElement;

  private kotletka: AnimatedSprite;
  private ship: Sprite;
  private bundle: AnimationAssets;

  private spaceship: Container;
  private emitter: ParticleEmitter;
  private portal: Sprite;

  private assetsConfig: Record<keyof AnimationAssets, string> = {
    particle: this.getFullPath('default-particle-2024-08-23-09-54-07.png'),
    fire: this.getFullPath('spaceship-fire-2024-10-07-14-14-18.json'),
    kotletka: this.getFullPath('kotletka-happy-2024-09-18-14-11-48.json'),
    spaceship: this.getFullPath('starship-diamond-2024-10-07-14-47-06.png'),
    portal: this.getFullPath('kotletka_portal-2024-11-06-14-20-51.png'),
  };

  constructor(private readonly config: Config, private readonly globalWindow: GlobalWindow) {}

  public async create(containerNode: HTMLElement): Promise<void> {
    Assets.addBundle('main-animation-bundle', this.assetsConfig);
    this.bundle = await Assets.loadBundle('main-animation-bundle');

    this.containerNode = containerNode;
    const {width, height} = containerNode.getBoundingClientRect();

    this.app = new Application();
    await this.app.init({
      preference: 'webgl',
      width,
      height,
      background: 0x000000,
      resolution: this.getAppDPR(),
      autoDensity: true,
      autoStart: true,
      antialias: true,
      resizeTo: this.containerNode,
      backgroundAlpha: 0,
    });

    const canvas = this.app.canvas as HTMLCanvasElement;
    containerNode.appendChild(canvas);

    // это нужно для локального использования браузерного расширения для пикси
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (globalThis as any).__PIXI_APP__ = this.app;

    this.rootContainer = this.app.stage;
    this.rootContainer.alpha = 0;

    const center = this.getCenter();

    const emitterContainer = new Container();

    const raysContainer = this.createRaysContainer();
    raysContainer.position = center.clone();

    this.animateRays(raysContainer);

    this.rootContainer.addChild(raysContainer);

    this.emitter = new ParticleEmitter<ContainerChild>(emitterContainer, this.createParticle, {
      emitterConfig: {spawnInterval: 150, maxParticles: 200},
      particleConfig: starsConfig(),
    });
    this.emitter.config.spawnPosition = center.clone();
    this.emitter.config.spawnParticlesPerWave = 200;
    this.emitter.emitWave();
    this.emitter.config.spawnParticlesPerWave = 5;

    this.rootContainer.addChild(emitterContainer);

    const spaceshipContainer = new Container();

    this.portal = this.createPortal();

    this.portal.x = center.x;
    this.portal.y = center.y;

    spaceshipContainer.addChild(this.portal);

    this.spaceship = this.createSpaceship();
    this.spaceship.x = center.x + 17;
    this.spaceship.y = center.y + 40;

    spaceshipContainer.addChild(this.spaceship);

    this.rootContainer.addChild(spaceshipContainer);

    this.animateSpaceship();

    this.rootContainer.alpha = 1;

    this.globalWindow.addEventListener('resize', this.handleResize);
  }

  public destroy(): void {
    this.app?.destroy();
    this.globalWindow.removeEventListener('resize', this.handleResize);
  }

  private createParticle = (): Sprite => {
    const particle = new Sprite(this.bundle.particle);
    particle.anchor.set(0.5);
    return particle;
  };

  private createRay(position: Point, scale: Point, angle: number): Sprite {
    const sprite = new Sprite(this.bundle.particle);

    sprite.position = position.clone();
    sprite.scale = scale.clone();
    sprite.angle = angle;
    sprite.tint = 0xe02dff;

    return sprite;
  }

  private handleResize = (): void => {
    const {width, height} = this.containerNode.getBoundingClientRect();

    const center = new Point(width / 2, height / 2);
    this.spaceship.x = center.x + 17;
    this.spaceship.y = center.y + 40;

    this.portal.x = center.x;
    this.portal.y = center.y;

    this.emitter.config.spawnPosition = center.clone();
  };

  private getAppDPR(): number {
    return window.devicePixelRatio;
  }

  private getCenter(): Point {
    const dimensions = this.getFrameDimensions();
    return new Point(dimensions.x / 2, dimensions.y / 2);
  }

  private getFrameDimensions(): Point {
    if (!this.app) return new Point(0, 0);

    return new Point(this.app.renderer.width, this.app.renderer.height);
  }

  private createSpaceship(): Container {
    const spaceshipFull = new Container();

    this.kotletka = new AnimatedSprite(GameObjectUtils.createFramesFromSpriteSheet(this.bundle.kotletka));
    this.kotletka.animationSpeed = 0.35;
    this.kotletka.loop = true;
    this.kotletka.anchor.set(0.5);
    this.kotletka.scale.set(0.6);
    this.kotletka.y = -25;
    this.kotletka.play();

    this.ship = new Sprite(this.bundle.spaceship);
    this.ship.anchor.set(0.5);
    this.ship.scale.set(0.55);

    const fire = new AnimatedSprite(GameObjectUtils.createFramesFromSpriteSheet(this.bundle.fire));
    fire.scale.set(0.6);
    fire.anchor.set(0.5);
    fire.y = 140;
    fire.loop = true;
    fire.play();

    const mask = new Graphics().ellipse(0, 0, 242 / 2, 326 / 2).fill({color: 0xffffff, alpha: 0.2});
    mask.angle = 29.5;
    mask.x = -12.5;
    mask.y = -14;

    this.ship.mask = mask;

    spaceshipFull.addChild(fire, this.kotletka, this.ship, mask);

    spaceshipFull.angle = -11;

    return spaceshipFull;
  }

  private createPortal(): Sprite {
    const portal = new Sprite(this.bundle.portal);
    portal.anchor.set(0.5);
    portal.scale.set(0.5);

    return portal;
  }

  private getFullPath(relativePath: string): string {
    return `${this.config.cdnUrl}/static/invest24/${relativePath}`;
  }

  private createRaysContainer(): Container {
    const raysContainer = new Container();

    raysContainer.addChild(
      this.createRay(new Point(-160, -121.5), new Point(0.585, 0.1), 43.5),
      this.createRay(new Point(-167, 34.5), new Point(0.385, 0.1), -13.5),
      this.createRay(new Point(-193, 113), new Point(0.385, 0.1), -27.5),
      this.createRay(new Point(-144, 188), new Point(0.385, 0.1), -41),
      this.createRay(new Point(144, 188), new Point(0.385, 0.1), 41),
      this.createRay(new Point(147, 118), new Point(0.635, 0.1), 41),
      this.createRay(new Point(147, -85), new Point(0.335, 0.1), -23),
      this.createRay(new Point(111.5, -182.5), new Point(0.685, 0.1), -55),
    );

    return raysContainer;
  }

  private animateRays(raysContainer: Container): void {
    anime({
      targets: raysContainer.children[0],
      x: raysContainer.children[0].x + RAYS_SHIFT,
      y: raysContainer.children[0].y + RAYS_SHIFT,
      direction: 'alternate',
      easing: RAYS_EASING,
      duration: RAYS_ANIMATION_DURATION,
      loop: true,
      delay: anime.random(RAYS_DELAY_MIN, RAYS_DELAY_MAX),
    });

    anime({
      targets: raysContainer.children[1],
      x: raysContainer.children[1].x - RAYS_SHIFT,
      y: raysContainer.children[1].y + RAYS_SHIFT,
      direction: 'alternate',
      easing: RAYS_EASING,
      duration: RAYS_ANIMATION_DURATION,
      loop: true,
      delay: anime.random(RAYS_DELAY_MIN, RAYS_DELAY_MAX),
    });

    anime({
      targets: raysContainer.children[2],
      x: raysContainer.children[2].x - RAYS_SHIFT,
      y: raysContainer.children[2].y + RAYS_SHIFT,
      direction: 'alternate',
      easing: RAYS_EASING,
      duration: RAYS_ANIMATION_DURATION,
      loop: true,
      delay: anime.random(RAYS_DELAY_MIN, RAYS_DELAY_MAX),
    });

    anime({
      targets: raysContainer.children[3],
      x: raysContainer.children[3].x - RAYS_SHIFT,
      y: raysContainer.children[3].y + RAYS_SHIFT,
      direction: 'alternate',
      easing: RAYS_EASING,
      duration: RAYS_ANIMATION_DURATION,
      loop: true,
      delay: anime.random(RAYS_DELAY_MIN, RAYS_DELAY_MAX),
    });

    anime({
      targets: raysContainer.children[4],
      x: raysContainer.children[4].x + RAYS_SHIFT,
      y: raysContainer.children[4].y + RAYS_SHIFT,
      direction: 'alternate',
      easing: RAYS_EASING,
      duration: RAYS_ANIMATION_DURATION,
      loop: true,
      delay: anime.random(RAYS_DELAY_MIN, RAYS_DELAY_MAX),
    });

    anime({
      targets: raysContainer.children[5],
      x: raysContainer.children[5].x + RAYS_SHIFT,
      y: raysContainer.children[5].y + RAYS_SHIFT,
      direction: 'alternate',
      easing: RAYS_EASING,
      duration: RAYS_ANIMATION_DURATION,
      loop: true,
      delay: anime.random(RAYS_DELAY_MIN, RAYS_DELAY_MAX),
    });

    anime({
      targets: raysContainer.children[6],
      x: raysContainer.children[6].x - RAYS_SHIFT,
      y: raysContainer.children[6].y + RAYS_SHIFT,
      direction: 'alternate',
      easing: RAYS_EASING,
      duration: RAYS_ANIMATION_DURATION,
      loop: true,
      delay: anime.random(RAYS_DELAY_MIN, RAYS_DELAY_MAX),
    });

    anime({
      targets: raysContainer.children[7],
      x: raysContainer.children[7].x - RAYS_SHIFT,
      y: raysContainer.children[7].y + RAYS_SHIFT,
      direction: 'alternate',
      easing: RAYS_EASING,
      duration: RAYS_ANIMATION_DURATION,
      loop: true,
      delay: anime.random(RAYS_DELAY_MIN, RAYS_DELAY_MAX),
    });
  }

  private animateSpaceship(): void {
    anime({
      targets: this.ship,
      y: [this.ship.y - SPACESHIP_ANIMATION_Y_SHIFT, this.ship.y + SPACESHIP_ANIMATION_Y_SHIFT],
      duration: SPACESHIP_ANIMATION_DURATION,
      loop: true,
      easing: SPACESHIP_ANIMATION_EASING,
      direction: 'alternate',
    });
  }
}

injected(MainPageAnimation, DI_TOKENS.config, DI_TOKENS.globalWindow);
